import { Box, VBox } from "@ui/box/Box";
import { Page } from "@ui/page/Page";
import { TabItem, Tabs } from "@ui/tabs/Tabs";
import { Calculator, PiggyBank, TrendingUp } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { GD } from "src/GD";
import { AccountantProfitCard } from "../statView/AccountantProfitCard";
import { PeriodSelector } from "../statView/PeriodSelector";
import { RollingReservePeriodCard } from "../statView/RollingReservePeriodCard";
import { statFilterSurface } from "../statView/panelStyles";
import { selectSharedPreset, selectSharedRange, useSharedPeriod } from "src/view/statView/sharedPeriod";

const formatDate = (date: Date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const periodBoundary = (index: number, boundary: "from" | "to") => {
	const now = new Date();
	if (index === 0) return formatDate(now);
	if (index === 1) { now.setDate(now.getDate() - 1); return formatDate(now); }
	if (index === 2) { if (boundary === "from") now.setDate(now.getDate() - 6); return formatDate(now); }
	if (index === 3) { if (boundary === "from") now.setDate(now.getDate() - 29); return formatDate(now); }
	if (index === 4) return formatDate(boundary === "from" ? new Date(now.getFullYear(), now.getMonth(), 1) : now);
	return formatDate(boundary === "from" ? new Date(now.getFullYear(), now.getMonth() - 1, 1) : new Date(now.getFullYear(), now.getMonth(), 0));
};

export const AccountantPage = () => {
	const { t } = useTranslation();
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);
	const { selectedDate, manualRange } = useSharedPeriod();
	const [currency, setCurrency] = useState("EUR");
	const dates = [t("dashboard.periods.today"), t("dashboard.periods.yesterday"), t("dashboard.periods.last7Days"), t("dashboard.periods.last30Days"), t("dashboard.periods.thisMonth"), t("dashboard.periods.lastMonth")];
	const from = manualRange?.from ?? periodBoundary(selectedDate, "from");
	const to = manualRange?.to ?? periodBoundary(selectedDate, "to");

	// Both tabs share one period; the rolling report has no currency filter, so only Profit offers one.
	const periodSelector = (showCurrency: boolean) => <PeriodSelector style={statFilterSurface} dates={dates} selectedDate={manualRange ? -1 : selectedDate} onDateChange={selectSharedPreset}
		from={from} to={to} onRangeChange={selectSharedRange}
		selectedCurrency={currency} onCurrencyChange={setCurrency} channels={[]} channelsBusy={false}
		selectedChannel="" onChannelChange={() => undefined} showCurrency={showCurrency} />;

	return <Page label={t("accountant.title")} description={t("accountant.description")} icon={<Calculator size={20} />}>
		<Box sx={{ minWidth: 0 }}>
			<VBox sx={{ gap: "20px", padding: "10px" }}>
				<Tabs variant="modern">
					<TabItem title={t("accountant.rollingTab")} icon={<PiggyBank size={16} />}>
						{periodSelector(false)}
						{project && user && <RollingReservePeriodCard key={`rolling:${project.id}:${from}:${to}`} projectId={project.id} userId={user.id} from={from} to={to} />}
					</TabItem>
					<TabItem title={t("accountant.profitTab")} icon={<TrendingUp size={16} />}>
						{periodSelector(true)}
						{project && user && <AccountantProfitCard key={`profit:${project.id}:${from}:${to}:${currency}`} projectId={project.id} userId={user.id} from={from} to={to} currency={currency} />}
					</TabItem>
				</Tabs>
			</VBox>
		</Box>
	</Page>;
};
